import type { ExecutionCandlesFetchParams } from "../services/tradeStatisticsApi";
import {
  executionCandleTypesMatch,
  normalizeExecutionCandleType,
  pickRequestedExecutionCandleType,
  toApiExecutionCandleType,
} from "./executionCandleTimeframe";
import type { ExecutionCandlesResponse } from "../types/trade";
import type { TradePeriodLocalRangeQuery } from "../types/tradePeriodApiContract";
import type { TradeListWindowQuery } from "./notificationPeriod";

export type ExecutionCandlesCohortIntent =
  | "trade_log"
  | "report"
  | "backtest"
  | "policy_exact";

/**
 * Whether to send `backtest_id` on execution-candles.
 *
 * Report-detail regime overlay (`cohortIntent === "report"`): omit so BE returns
 * the full report snapshot. Simulator / backtest surfaces must keep `backtest_id`
 * even when a linked `report_id` exists — report-only requests 404 when the report
 * is missing ("Report wasn't found").
 */
export function resolveExecutionCandlesBacktestId(args: {
  backtestId?: string | null;
  reportIdForCohort?: string | null;
  cohortIntent?: ExecutionCandlesCohortIntent | null;
  regimePolicyOverlayActive: boolean;
  isBaselineVsSimulatedOverlay?: boolean;
}): string | undefined {
  const id = args.backtestId?.trim();
  if (!id) return undefined;
  if (
    args.regimePolicyOverlayActive &&
    args.reportIdForCohort?.trim() &&
    !args.isBaselineVsSimulatedOverlay &&
    args.cohortIntent === "report"
  ) {
    return undefined;
  }
  return id;
}

export type ExecutionCandlesRequestWindow =
  | { kind: "date"; date: string; reportingTimeZone?: string }
  | {
      kind: "range";
      start_date: string;
      end_date: string;
      reportingTimeZone?: string;
    }
  | {
      kind: "local";
      period: TradePeriodLocalRangeQuery;
    }
  | {
      kind: "timeframe";
      timeframe: string;
      reportingTimeZone?: string;
    }
  | {
      kind: "trade_list";
      window: TradeListWindowQuery;
    };

export type BuildExecutionCandlesParamsInput = {
  symbol: string;
  brokerAccountCode: string;
  brokerName?: string | null;
  candleType?: string;
  asset_class?: string;
  /** Explicit chart chunk from suggested_periods. */
  periodWindow?: { start: string; end: string } | null;
  /** Parent-provided window (trade log, report, backtest scope). */
  requestWindow?: ExecutionCandlesRequestWindow | null;
  reportIdForCohort?: string | null;
  /** Demo Entry Analysis job — routes candles to demo execution-candles (poll cookie). */
  demoJobId?: string | null;
  exactTradeIds?: string[] | null;
  focusTradeId?: string | null;
  backtestId?: string | null;
  cohortIntent?: ExecutionCandlesCohortIntent;
  /** Opt-in regime bgcolor series on chart candle TF. */
  includeRegimeOverlay?: boolean;
};

const INVALID_REPORT_ID_PLACEHOLDERS = new Set(["simulator", ""]);

/**
 * Soft cap for `trade_ids` on GET /execution-candles.
 * Prefer `backtest_id` / `report_id` for larger cohorts — giant CSV query strings die in Safari.
 */
export const EXECUTION_CANDLES_TRADE_IDS_SOFT_CAP = 50;

/** Backend hard max; never serialize more than this into the URL. */
export const EXECUTION_CANDLES_TRADE_IDS_HARD_MAX = 2000;

/**
 * Keep only small explicit `trade_ids` lists for the GET query.
 * Large sets must use `backtest_id` / `report_id` (resolved server-side).
 */
export function sanitizeExecutionCandlesTradeIds(
  ids: string[] | null | undefined,
  opts?: { hasReportId?: boolean; hasBacktestId?: boolean; hasDemoJobId?: boolean }
): string[] | undefined {
  const cleaned =
    ids?.map((id) => id.trim()).filter(Boolean) ?? [];
  if (cleaned.length === 0) return undefined;

  if (cleaned.length <= EXECUTION_CANDLES_TRADE_IDS_SOFT_CAP) {
    return cleaned;
  }

  // Demo job candles have no report_id cohort — allow up to hard max on the demo GET.
  if (opts?.hasDemoJobId && cleaned.length <= EXECUTION_CANDLES_TRADE_IDS_HARD_MAX) {
    return cleaned;
  }

  const hasCohortUuid = Boolean(opts?.hasReportId || opts?.hasBacktestId);
  if (typeof console !== "undefined" && typeof console.warn === "function") {
    console.warn(
      `[execution-candles] refusing trade_ids=${cleaned.length} (soft cap ${EXECUTION_CANDLES_TRADE_IDS_SOFT_CAP})` +
        (hasCohortUuid
          ? " — using backtest_id/report_id cohort instead"
          : " — pass backtest_id or report_id instead of a giant trade_ids CSV")
    );
  }
  return undefined;
}

/** Rolling presets accepted by GET /api/trades/execution-candles (FastAPI route regex). */
export const EXECUTION_CANDLES_ALLOWED_TIMEFRAMES = [
  "today",
  "7-days",
  "14-days",
  "30-days",
  "all-time",
] as const;

export type ExecutionCandlesAllowedTimeframe =
  (typeof EXECUTION_CANDLES_ALLOWED_TIMEFRAMES)[number];

export function isAllowedExecutionCandlesTimeframe(
  timeframe: string | null | undefined
): timeframe is ExecutionCandlesAllowedTimeframe {
  if (!timeframe?.trim()) return false;
  return (EXECUTION_CANDLES_ALLOWED_TIMEFRAMES as readonly string[]).includes(timeframe.trim());
}

export type ReportTradeReviewWindowSource = {
  timeframe?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  timezone?: string | null;
  reporting_timezone?: string | null;
  cohort_period?: {
    local_start_date?: string;
    local_end_date?: string;
    timezone?: string;
  } | null;
  input_snapshot?: unknown;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function pickNonEmptyString(...candidates: unknown[]): string | null {
  for (const candidate of candidates) {
    if (typeof candidate === "string") {
      const trimmed = candidate.trim();
      if (trimmed) return trimmed;
    }
  }
  return null;
}

function calendarYmdFromIso(value: string | null | undefined): string | null {
  if (!value?.trim()) return null;
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(value.trim());
  return match?.[1] ?? null;
}

/**
 * Trade Review window for report-origin execution-candles.
 * Prefer snapshot / cohort local dates; never send `timeframe=custom`.
 */
export function resolveReportTradeReviewRequestWindow(
  source: ReportTradeReviewWindowSource,
  fallbackTimezone?: string | null
): {
  timeframe?: string;
  local_start_date?: string;
  local_end_date?: string;
  timezone?: string;
} {
  const snapshot = asRecord(source.input_snapshot);
  const snapshotRequest = asRecord(snapshot?.request);
  const snapshotMeta = asRecord(snapshot?.meta);
  const cohortPeriod =
    source.cohort_period ??
    (asRecord(snapshotMeta?.cohort_period) as ReportTradeReviewWindowSource["cohort_period"]) ??
    (asRecord(snapshotRequest?.cohort_period) as ReportTradeReviewWindowSource["cohort_period"]);

  const timezone =
    pickNonEmptyString(
      source.timezone,
      source.reporting_timezone,
      cohortPeriod?.timezone,
      snapshotRequest?.timezone,
      snapshotMeta?.timezone,
      fallbackTimezone
    ) ?? undefined;

  const localStart = pickNonEmptyString(
    cohortPeriod?.local_start_date,
    snapshotRequest?.local_start_date,
    snapshotMeta?.local_start_date,
    calendarYmdFromIso(source.start_date)
  );
  const localEnd = pickNonEmptyString(
    cohortPeriod?.local_end_date,
    snapshotRequest?.local_end_date,
    snapshotMeta?.local_end_date,
    calendarYmdFromIso(source.end_date)
  );

  if (localStart && localEnd && /^\d{4}-\d{2}-\d{2}$/.test(localStart) && /^\d{4}-\d{2}-\d{2}$/.test(localEnd)) {
    return {
      local_start_date: localStart,
      local_end_date: localEnd,
      timezone,
    };
  }

  const timeframe = source.timeframe?.trim();
  if (timeframe && isAllowedExecutionCandlesTimeframe(timeframe)) {
    return { timeframe, timezone };
  }

  return { timeframe: "all-time", timezone };
}

function normalizeReportId(reportId?: string | null): string | undefined {
  const trimmed = reportId?.trim();
  if (!trimmed || INVALID_REPORT_ID_PLACEHOLDERS.has(trimmed.toLowerCase())) {
    return undefined;
  }
  return trimmed;
}

function applyWindow(
  out: Omit<ExecutionCandlesFetchParams, "symbol" | "broker_account_code">,
  input: BuildExecutionCandlesParamsInput
): void {
  if (input.periodWindow) {
    out.start_date = input.periodWindow.start;
    out.end_date = input.periodWindow.end;
    return;
  }

  const rw = input.requestWindow;
  if (!rw) return;

  switch (rw.kind) {
    case "date":
      out.date = rw.date;
      if (rw.reportingTimeZone) out.reporting_time_zone = rw.reportingTimeZone;
      break;
    case "range":
      out.start_date = rw.start_date;
      out.end_date = rw.end_date;
      if (rw.reportingTimeZone) out.reporting_time_zone = rw.reportingTimeZone;
      break;
    case "local":
      out.local_start_date = rw.period.local_start_date;
      out.local_end_date = rw.period.local_end_date;
      if (rw.period.timezone) out.reporting_time_zone = rw.period.timezone;
      break;
    case "timeframe":
      if (isAllowedExecutionCandlesTimeframe(rw.timeframe)) {
        out.timeframe = rw.timeframe;
      }
      if (rw.reportingTimeZone) out.reporting_time_zone = rw.reportingTimeZone;
      break;
    case "trade_list":
      if (rw.window.kind === "local") {
        out.local_start_date = rw.window.period.local_start_date;
        out.local_end_date = rw.window.period.local_end_date;
        if (rw.window.period.timezone) {
          out.reporting_time_zone = rw.window.period.timezone;
        }
      } else if (isAllowedExecutionCandlesTimeframe(rw.window.timeframe)) {
        out.timeframe = rw.window.timeframe;
      }
      break;
    default:
      break;
  }
}

/**
 * Build execution-candles fetch params for Trade Review surfaces.
 * Enforces cohort mutual exclusion and always sets fill_missing=true.
 */
export function buildExecutionCandlesParams(
  input: BuildExecutionCandlesParamsInput
): ExecutionCandlesFetchParams | null {
  const demoJobId = input.demoJobId?.trim() || undefined;
  if (!input.symbol?.trim()) return null;
  if (!demoJobId && !input.brokerAccountCode?.trim()) return null;

  let reportId = normalizeReportId(input.reportIdForCohort);
  const backtestId = input.backtestId?.trim() || undefined;

  // Soft-cap trade_ids; large cohorts must use backtest_id / report_id (or demo job path).
  let exactTradeIds = sanitizeExecutionCandlesTradeIds(input.exactTradeIds, {
    hasReportId: Boolean(reportId),
    hasBacktestId: Boolean(backtestId),
    hasDemoJobId: Boolean(demoJobId),
  });

  if (backtestId && exactTradeIds && exactTradeIds.length > 0) {
    throw new Error("execution-candles: backtest_id cohort replaces trade_ids — omit trade_ids");
  }

  // Prefer small explicit trade_ids (cohort preview). report_id + trade_ids → 400.
  if (reportId && exactTradeIds && exactTradeIds.length > 0) {
    if (typeof console !== "undefined" && typeof console.warn === "function") {
      console.warn(
        "[execution-candles] report_id and trade_ids both set — using trade_ids only"
      );
    }
    reportId = undefined;
  }

  // Demo Market preview: always exact trade_ids via demo job endpoint (no production report cohort).
  if (demoJobId) {
    if (!exactTradeIds?.length) return null;
    const demoParams: ExecutionCandlesFetchParams = {
      symbol: input.symbol.trim(),
      broker_account_code: input.brokerAccountCode?.trim() || "demo",
      demo_job_id: demoJobId,
      trade_ids: exactTradeIds,
      fill_missing: false,
    };
    if (input.candleType) {
      const apiCandleType = toApiExecutionCandleType(input.candleType);
      if (apiCandleType) demoParams.candle_type = apiCandleType;
    }
    if (input.includeRegimeOverlay) {
      demoParams.include_regime_overlay = true;
    }
    return demoParams;
  }

  const params: ExecutionCandlesFetchParams = {
    symbol: input.symbol.trim(),
    broker_account_code: input.brokerAccountCode.trim(),
    fill_missing: true,
  };

  if (input.brokerName?.trim()) {
    params.broker_name = input.brokerName.trim().toLowerCase();
  }
  if (input.candleType) {
    const apiCandleType = toApiExecutionCandleType(input.candleType);
    if (apiCandleType) params.candle_type = apiCandleType;
  }
  if (input.asset_class) params.asset_class = input.asset_class;

  // Report / backtest cohort ids (window applied below).
  if (reportId && input.cohortIntent === "report") {
    params.report_id = reportId;
  } else if (reportId && input.cohortIntent === "backtest") {
    // Report-origin backtest: both ids — BE resolves baseline from backtest_id (kept_trade_ids).
    params.report_id = reportId;
  } else if (reportId) {
    params.report_id = reportId;
  }

  if (exactTradeIds && exactTradeIds.length > 0 && !backtestId) {
    params.trade_ids = exactTradeIds;
  }

  if (input.focusTradeId?.trim()) {
    params.focus_trade_id = input.focusTradeId.trim();
  }

  if (backtestId) {
    params.backtest_id = backtestId;
  }

  if (input.includeRegimeOverlay) {
    params.include_regime_overlay = true;
  }

  applyWindow(params, input);

  if (params.timeframe && !isAllowedExecutionCandlesTimeframe(params.timeframe)) {
    delete params.timeframe;
  }

  if (reportId && input.cohortIntent === "report" && !input.periodWindow) {
    const hasWindow = Boolean(
      params.date ||
        (params.start_date && params.end_date) ||
        (params.local_start_date && params.local_end_date) ||
        params.timeframe
    );
    if (!hasWindow) {
      params.timeframe = "all-time";
    }
  }

  const hasWindow = Boolean(
    params.date ||
      (params.start_date && params.end_date) ||
      (params.local_start_date && params.local_end_date) ||
      params.timeframe
  );

  if (!hasWindow) return null;

  return params;
}

/** Stable cache identity for execution-candles (excludes abort signal / timeout). */
export function executionCandlesParamsCachePayload(
  params: ExecutionCandlesFetchParams
): Omit<ExecutionCandlesFetchParams, "signal" | "timeoutMs"> {
  const { signal: _signal, timeoutMs: _timeoutMs, ...cacheable } = params;
  return cacheable;
}

export function executionCandlesCacheMatchesParams(
  cachedData: ExecutionCandlesResponse,
  params: ExecutionCandlesFetchParams
): boolean {
  const requested = normalizeExecutionCandleType(params.candle_type);
  if (!requested) return true;
  const cachedRequested = pickRequestedExecutionCandleType(
    cachedData,
    params.candle_type ?? ""
  );
  return executionCandleTypesMatch(requested, cachedRequested);
}

const COHORT_AXIS_BY_INTENT: Partial<
  Record<ExecutionCandlesCohortIntent, string[]>
> = {
  trade_log: ["exit_date", "symbol_all_time", "symbol_lifespan"],
  report: ["report"],
  backtest: ["backtest", "report", "exit_date", "symbol_all_time", "symbol_lifespan"],
  policy_exact: ["trade_ids"],
};

/** Dev-only warning when BE cohort axis disagrees with FE intent. */
export function warnExecutionCandlesCohortMismatch(
  intent: ExecutionCandlesCohortIntent | undefined,
  axis: string | undefined
): void {
  if (!intent || !axis || process.env.NODE_ENV === "production") return;
  const allowed = COHORT_AXIS_BY_INTENT[intent];
  if (allowed && !allowed.includes(axis)) {
    console.warn(
      `[execution-candles] trades_cohort_axis "${axis}" unexpected for intent "${intent}" (expected one of ${allowed.join(", ")})`
    );
  }
}
